// frontend/src/pages/championships/ChampionshipPlayerForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import championshipPlayerService from '../../services/championshipPlayerService';
import useAuth from '../../hooks/useAuth'; // Para verificar permissões
import './ChampionshipPlayerForm.css'; // Crie este arquivo CSS (se não existir, é bom criar um básico)

function ChampionshipPlayerForm() {
  const { championshipPlayerId } = useParams(); // ID da inscrição específica
  const navigate = useNavigate();
  const { user, loadingAuth } = useAuth();

  const [formData, setFormData] = useState({
    position: '',
    shirtNumber: '',
    // <<<<<<< ADICIONADOS NOVOS CAMPOS PARA EDIÇÃO (do modelo Player)
    nickname: '',
    birthDate: '', // Formato YYYY-MM-DD para input type="date"
    phone: '',
    // <<<<<<< FIM NOVOS CAMPOS
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [championshipPlayer, setChampionshipPlayer] = useState(null); // Para exibir outros dados
  const [championshipName, setChampionshipName] = useState(''); // Nome do campeonato
  const [teamName, setTeamName] = useState(''); // Nome do time
  const [playerName, setPlayerName] = useState(''); // Nome do jogador

  const positions = [
    'Goleiro', 'Zagueiro', 'Lateral', 'Volante', 
    'Meia', 'Atacante', 'Técnico', 'Auxiliar Técnico', 'Diretor'
  ];

  const canEdit = useMemo(() => {
    // A permissão para editar é controlada pelo backend, mas o frontend pode desabilitar o form
    // se o usuário não for admin, gestor, diretor ou técnico.
    if (loadingAuth || !user) return false;
    const userRole = user.role?.toLowerCase();
    // Este é um check básico de frontend. A autorização completa é feita no backend.
    return ['admin', 'gestor', 'diretor', 'técnico'].includes(userRole);
  }, [user, loadingAuth]);

  useEffect(() => {
    const fetchChampionshipPlayerData = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await championshipPlayerService.getChampionshipPlayerById(championshipPlayerId);
        
        setChampionshipPlayer(data);
        
        // <<<<<<< CORREÇÃO AQUI: O campeonato, time e jogador agora estão populados pelo backend
        setChampionshipName(data.championship?.name || 'N/A');
        setTeamName(data.team?.name || 'N/A');
        setPlayerName(data.player?.name || 'N/A');

        // Formata a data de nascimento para o input type="date"
        const formattedBirthDate = data.player?.birthDate 
          ? new Date(data.player.birthDate).toISOString().split('T')[0] 
          : '';

        setFormData({
          position: data.position || '',
          shirtNumber: data.shirtNumber || '',
          // <<<<<<< ATUALIZAÇÃO PARA OS NOVOS CAMPOS DO JOGADOR
          nickname: data.player?.nickname || '',
          birthDate: formattedBirthDate,
          phone: data.player?.phone || '',
          // <<<<<<< FIM ATUALIZAÇÃO NOVOS CAMPOS
        });
      } catch (err) {
        setError(err.message || 'Erro ao carregar dados da inscrição.');
        console.error('Erro ao buscar ChampionshipPlayer:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchChampionshipPlayerData();
  }, [championshipPlayerId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) {
      setError('Você não tem permissão para editar esta inscrição.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      await championshipPlayerService.updateChampionshipPlayer(championshipPlayerId, formData);
      alert('Inscrição atualizada com sucesso!');
      navigate(-1); // Volta para a página anterior
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Erro ao atualizar inscrição.');
      console.error('Erro ao atualizar ChampionshipPlayer:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || loadingAuth) return <div className="loading-form">Carregando formulário...</div>;
  if (error) return <div className="error-form">Erro: {error}</div>;
  if (!championshipPlayer) return <div className="error-form">Inscrição não encontrada.</div>;

  return (
    <div className="championship-player-form-container">
      <h1 className="form-title">Editar Inscrição de Jogador</h1>

      <div className="player-info-display">
        <p><strong>Campeonato:</strong> {championshipName}</p>
        <p><strong>Time:</strong> {teamName}</p>
        <p><strong>Jogador (CPF):</strong> {championshipPlayer.player?.name || 'N/A'} ({championshipPlayer.player?.cpf || 'N/A'})</p>
      </div>

      <form onSubmit={handleSubmit} className="championship-player-form">
        {/* <<<<<<< NOVOS CAMPOS DE EDIÇÃO DE DADOS DO JOGADOR */}
        <div className="form-group">
          <label htmlFor="nickname" className="form-label">Apelido:</label>
          <input
            type="text"
            id="nickname"
            name="nickname"
            value={formData.nickname}
            onChange={handleChange}
            className="form-input"
            disabled={submitting || !canEdit}
            maxLength="50"
          />
        </div>

        <div className="form-group">
          <label htmlFor="birthDate" className="form-label">Data de Nascimento:</label>
          <input
            type="date"
            id="birthDate"
            name="birthDate"
            value={formData.birthDate}
            onChange={handleChange}
            className="form-input"
            disabled={submitting || !canEdit}
            required
          />
        </div>

        <div className="form-group">
          <label htmlFor="phone" className="form-label">Telefone (apenas números, DDD + Número):</label>
          <input
            type="tel" // Use type="tel" para melhor acessibilidade em dispositivos móveis
            id="phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="form-input"
            disabled={submitting || !canEdit}
            maxLength="11" // Ex: 11987654321
            pattern="\d{10,11}" // Permite 10 ou 11 dígitos numéricos para validação básica no cliente
            title="Telefone deve ter 10 ou 11 dígitos numéricos"
          />
        </div>
        {/* <<<<<<< FIM NOVOS CAMPOS */}

        <div className="form-group">
          <label htmlFor="position" className="form-label">Posição (Neste Campeonato):</label>
          <select 
            id="position" 
            name="position" 
            value={formData.position} 
            onChange={handleChange} 
            className="form-select"
            disabled={submitting || !canEdit}
            required
          >
            <option value="">Selecione a posição</option>
            {positions.map(pos => (
              <option key={pos} value={pos}>{pos}</option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="shirtNumber" className="form-label">Número da Camisa (Neste Campeonato, Opcional):</label>
          <input
            type="number"
            id="shirtNumber"
            name="shirtNumber"
            value={formData.shirtNumber}
            onChange={handleChange}
            className="form-input"
            min="0"
            max="999"
            disabled={submitting || !canEdit}
          />
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => navigate(-1)} 
            className="cancel-button" 
            disabled={submitting}
          >
            Voltar
          </button>
          <button 
            type="submit" 
            className="submit-button" 
            disabled={submitting || !canEdit}
          >
            {submitting ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default ChampionshipPlayerForm;